import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, UserCheck } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="bg-white pt-16">
      {/* Hero Section */}
      <section className="relative py-20 px-8 lg:px-16 overflow-hidden bg-gradient-to-br from-[#0a0015] via-[#1a0a2e] to-[#0f0a1a]">
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 mb-6 px-6 py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
              <Shield size={20} className="text-light-pink" />
              <span className="text-sm font-mono uppercase tracking-wider text-white">Legal</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white leading-tight tracking-tight" style={{ fontWeight: 700 }}>
              Privacy Policy
            </h1>
            
            <p className="text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
              Your privacy is important to us. This policy explains how SYNCORE LLC collects, uses, and protects your information.
            </p>
            
            <p className="text-sm text-white/60 mt-4">
              Last updated: January 1, 2025
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-8 lg:px-16">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-light-pink/20 flex items-center justify-center">
                  <Eye size={24} className="text-light-pink" />
                </div>
                <h2 className="text-3xl font-bold mb-0">Introduction</h2>
              </div>
              <p className="text-lg text-dark-grey leading-relaxed">
                SYNCORE LLC ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
              </p>
            </motion.div>

            {/* Information We Collect */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-mid-purple/20 flex items-center justify-center">
                  <UserCheck size={24} className="text-mid-purple" />
                </div>
                <h2 className="text-3xl font-bold mb-0">Information We Collect</h2>
              </div>
              
              <h3 className="text-xl font-bold mb-4">Personal Information</h3>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                We may collect personal information that you voluntarily provide when you:
              </p>
              <ul className="text-lg text-dark-grey mb-6 pl-6">
                <li className="mb-2">• Fill out contact forms</li>
                <li className="mb-2">• Request quotes or services</li>
                <li className="mb-2">• Subscribe to our newsletter</li>
                <li className="mb-2">• Contact us via email or phone</li>
              </ul>

              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                This information may include:
              </p>
              <ul className="text-lg text-dark-grey mb-6 pl-6">
                <li className="mb-2">• Name and contact information</li>
                <li className="mb-2">• Email address</li>
                <li className="mb-2">• Phone number</li>
                <li className="mb-2">• Company information</li>
                <li className="mb-2">• Project requirements and preferences</li>
              </ul>

              <h3 className="text-xl font-bold mb-4">Automatically Collected Information</h3>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                When you visit our website, we may automatically collect certain information about your device and usage:
              </p>
              <ul className="text-lg text-dark-grey mb-6 pl-6">
                <li className="mb-2">• IP address</li>
                <li className="mb-2">• Browser type and version</li>
                <li className="mb-2">• Operating system</li>
                <li className="mb-2">• Referring website</li>
                <li className="mb-2">• Pages viewed and time spent</li>
                <li className="mb-2">• Device information</li>
              </ul>
            </motion.div>

            {/* How We Use Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">How We Use Your Information</h2>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                We use collected information for the following purposes:
              </p>
              <ul className="text-lg text-dark-grey mb-6 pl-6">
                <li className="mb-2">• Respond to your inquiries and provide customer support</li>
                <li className="mb-2">• Deliver services and communicate about projects</li>
                <li className="mb-2">• Send newsletters and marketing communications (with consent)</li>
                <li className="mb-2">• Improve our website and services</li>
                <li className="mb-2">• Analyze website usage and trends</li>
                <li className="mb-2">• Comply with legal obligations</li>
                <li className="mb-2">• Prevent fraud and ensure security</li>
              </ul>
            </motion.div>

            {/* Information Sharing */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">Information Sharing</h2>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
              </p>
              <ul className="text-lg text-dark-grey mb-6 pl-6">
                <li className="mb-2">• With your consent</li>
                <li className="mb-2">• To comply with legal requirements</li>
                <li className="mb-2">• To protect our rights and safety</li>
                <li className="mb-2">• With trusted service providers who assist our operations</li>
                <li className="mb-2">• In connection with business transfers</li>
              </ul>
            </motion.div>

            {/* Data Security */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-light-blue/20 flex items-center justify-center">
                  <Lock size={24} className="text-light-blue" />
                </div>
                <h2 className="text-3xl font-bold mb-0">Data Security</h2>
              </div>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                We implement appropriate security measures to protect your information against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="text-lg text-dark-grey mb-6 pl-6">
                <li className="mb-2">• SSL encryption for data transmission</li>
                <li className="mb-2">• Secure servers and databases</li>
                <li className="mb-2">• Regular security assessments</li>
                <li className="mb-2">• Limited access to personal information</li>
                <li className="mb-2">• Employee training on data protection</li>
              </ul>
            </motion.div>

            {/* Your Rights */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">Your Rights</h2>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="text-lg text-dark-grey mb-6 pl-6">
                <li className="mb-2">• Access your personal information</li>
                <li className="mb-2">• Correct inaccurate information</li>
                <li className="mb-2">• Request deletion of your information</li>
                <li className="mb-2">• Opt-out of marketing communications</li>
                <li className="mb-2">• Request data portability</li>
                <li className="mb-2">• Object to processing of your information</li>
              </ul>
              <p className="text-lg text-dark-grey leading-relaxed">
                To exercise these rights, please contact us at privacy@syncore.com.
              </p>
            </motion.div>

            {/* Cookies */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">Cookies and Tracking</h2>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                We use cookies and similar technologies to enhance your browsing experience and analyze website usage. You can control cookie settings through your browser preferences.
              </p>
            </motion.div>

            {/* Third Party Services */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">Third-Party Services</h2>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                Our website may contain links to third-party websites or integrate with third-party services. We are not responsible for the privacy practices of these external sites.
              </p>
            </motion.div>

            {/* Changes to Policy */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">Changes to This Policy</h2>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date.
              </p>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-grey rounded-2xl p-8"
            >
              <h2 className="text-3xl font-bold mb-6">Contact Us</h2>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="text-lg text-dark-grey">
                <p className="mb-2"><strong>SYNCORE LLC</strong></p>
                <p className="mb-2">Email: info@syncore.com</p>
                <p>Address: 525 W Main St, Visalia, CA 93291, United States</p>
              </div>
            </motion.div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;